import SecondaryAlbumTitle from '../../../../components/content/MeasuredContentTitle';
import RecoverableImage from '../../../../features/images/RecoverableImage';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { useDetailReturn } from '../detail/DetailReturnState';
import { albumApi } from '../../../../api/album';
import { artistApi } from '../../../../api/artist';
import { useUserStore } from '../../../../stores/userStore';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import AppUiScope from '../../../../app-ui/AppUiScope';
import baseline, { appTextStyle } from '../../../../app-ui/baseline';
import AppContentSection from '../../../../app-ui/AppContentSection';
import ContentShelf, {
  ShelfNavigationContext,
} from '../../../../components/responsive/ContentShelf';
import { artistPath } from '../../../../features/browse/artist';
import { BrowseInlineAd } from '../../../../features/browse/BrowseSponsored';
import { useSponsoredPlacements } from '../../../../features/ads/useSponsoredPlacements';
import { RatingStar } from './AlbumActionEditor';
import {
  albumIdentity,
  otherAlbumVersions,
  artistAlbumSelection,
  canLoadAlbumArtist,
  secondaryAlbumPath,
} from './albumSecondaryModel';
import '../../../../features/browse/Browse.css';
import './AlbumSecondaryContent.css';

export { default as SecondaryAlbumTitle } from '../../../../components/content/MeasuredContentTitle';

export function SecondaryAlbumCard({ album, artistName, focusKey }) {
  const { locale } = useWebLocale();
  const image = album.albumImageUrl || album.imageUrl;
  return (
    <Link
      data-browse-focus-key={focusKey}
      className="pp-secondary-album"
      to={secondaryAlbumPath(album)}
    >
      <span className="pp-secondary-artwork">
        postpop.
        {image && <RecoverableImage src={image} alt="" loading="lazy" />}
      </span>
      <span className="pp-secondary-copy">
        <SecondaryAlbumTitle title={album.title} explicit={album.contentRating === 'EXPLICIT'} />
        <span className="pp-secondary-artist" style={appTextStyle(baseline.contentText.artist)}>
          {artistName ??
            album.artist?.name ??
            (locale === 'en' ? 'Artist information unavailable' : '아티스트 정보 없음')}
        </span>
        <span className="pp-secondary-date" style={appTextStyle(baseline.contentText.metadata)}>
          {album.releaseDate || ''}
        </span>
        <span className="pp-secondary-score" style={appTextStyle(baseline.contentText.score)}>
          <RatingStar value={1} size={baseline.contentRating.starSize} />
          {Number(album.averageRating || 0).toFixed(1)}
        </span>
      </span>
    </Link>
  );
}
function Rail({ title, query, items, to, artistName, memoryKey }) {
  const { locale } = useWebLocale(),
    en = locale === 'en';
  const container = useRef(null);
  const { entry, returning } = useDetailReturn() || {};
  useLayoutEffect(() => {
    const node = container.current?.querySelector('.pp-shelf-track');
    if (!node) return;
    if (returning) node.scrollLeft = entry?.rails?.[memoryKey] || 0;
    const save = () => {
      if (entry?.current()) (entry.rails ||= {})[memoryKey] = node.scrollLeft;
    };
    node.addEventListener('scroll', save, { passive: true });
    return () => {
      node.removeEventListener('scroll', save);
      save();
    };
  }, [memoryKey, entry, returning, items.length, query.isError]);
  if (!query.isLoading && !query.isError && !items.length) return null;
  return (
    <div ref={container}>
      <AppContentSection
        title={title}
        to={!query.isError && items.length ? to : undefined}
        inlineMore
      >
        {query.isError ? (
          <div className="pp-secondary-state" role="alert">
            <span>{en ? 'Could not load this section.' : '이 영역을 불러오지 못했어요.'}</span>
            <button onClick={() => query.refetch()}>{en ? 'Try Again' : '다시 시도'}</button>
          </div>
        ) : query.isLoading ? (
          <div
            className="pp-secondary-skeleton"
            role="status"
            aria-label={en ? 'Loading' : '불러오는 중'}
          >
            <span />
            <span />
            <span />
          </div>
        ) : (
          <ContentShelf label={title}>
            {items.map((item) => (
              <SecondaryAlbumCard
                key={albumIdentity(item)}
                album={item}
                artistName={artistName}
                focusKey={`${memoryKey}:${albumIdentity(item)}`}
              />
            ))}
          </ContentShelf>
        )}
      </AppContentSection>
    </div>
  );
}
function SecondaryReady({ album }) {
  const { locale } = useWebLocale(),
    en = locale === 'en',
    location = useLocation();
  const userId = useUserStore((s) => s.user?.id),
    client = useQueryClient();
  const versions = useQuery({
    queryKey: ['otherVersions', album.sourceId, album.albumSource],
    queryFn: () =>
      albumApi.getOtherVersions(album.sourceId, album.albumSource, { suppressGlobalError: true }),
    select: (r) => r.data,
    staleTime: 300000,
  });
  const related = useQuery({
    queryKey: ['relatedAlbums', album.sourceId, album.albumSource],
    queryFn: () =>
      albumApi.getRelatedAlbums(album.sourceId, album.albumSource, { suppressGlobalError: true }),
    select: (r) => r.data,
  });
  const artist = album.artist,
    enabled = canLoadAlbumArtist(artist);
  const discography = useQuery({
    queryKey: ['album-artist-discography', artist?.slug, artist?.sourceId, artist?.artistSource],
    enabled,
    queryFn: () =>
      artist.slug
        ? artistApi.getArtistBySlug(
            artist.slug,
            {
              sourceId: artist.sourceId,
              sourceType: artist.artistSource,
            },
            { suppressGlobalError: true },
          )
        : artistApi.getArtistById(artist.artistSource, artist.sourceId, {
            suppressGlobalError: true,
          }),
    select: (r) => r.data,
  });
  const refreshed = useRef(0);
  useEffect(() => {
    if (
      !userId ||
      !album.id ||
      album.versionGroupId != null ||
      !versions.data?.length ||
      refreshed.current === versions.dataUpdatedAt
    )
      return;
    refreshed.current = versions.dataUpdatedAt;
    void client.invalidateQueries({
      queryKey: ['albumDetail', album.albumSource.toLowerCase(), album.sourceId],
    });
  }, [
    userId,
    album.id,
    album.versionGroupId,
    album.albumSource,
    album.sourceId,
    versions.data?.length,
    versions.dataUpdatedAt,
    client,
  ]);
  const other = otherAlbumVersions(versions.data, album),
    artistItems = artistAlbumSelection(discography.data, album, other);
  const campaigns = useSponsoredPlacements('ALBUM_DETAIL');
  const key = location.pathname + location.search;
  const settled = !versions.isLoading && !related.isLoading && (!enabled || !discography.isLoading);
  const title = artist?.name
    ? en
      ? `More Albums by ${artist.name}`
      : `${artist.name}의 다른 앨범`
    : en
      ? 'More Albums by This Artist'
      : '아티스트의 다른 앨범';
  if (
    settled &&
    !versions.isError &&
    !related.isError &&
    (!enabled || !discography.isError) &&
    !other.length &&
    !artistItems.length &&
    !related.data?.length &&
    !campaigns.length
  )
    return null;
  return (
    <div className="pp-album-secondary-wrap">
      <AppUiScope className="pp-album-secondary" surface="lists">
        <ShelfNavigationContext.Provider value="edges">
          <Rail
            title={en ? 'Other Versions' : '다른 버전'}
            query={versions}
            items={other}
            artistName={artist?.name}
            memoryKey={`${key}:versions`}
          />
          {enabled && (
            <Rail
              title={title}
              query={discography}
              items={artistItems}
              to={artistPath(artist)}
              memoryKey={`${key}:artist`}
            />
          )}
          {campaigns.map((campaign) => (
            <BrowseInlineAd key={campaign.id} campaign={campaign} />
          ))}
          <Rail
            title={en ? 'Related Albums' : '관련 앨범'}
            query={related}
            items={(related.data || []).slice(0, 12)}
            memoryKey={`${key}:related`}
          />
        </ShelfNavigationContext.Provider>
      </AppUiScope>
    </div>
  );
}
function DelayedSecondary({ album }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(timer);
  }, []);
  return ready ? <SecondaryReady album={album} /> : null;
}
export default function AlbumSecondaryContent({ album }) {
  return album ? <DelayedSecondary key={albumIdentity(album)} album={album} /> : null;
}
